import { Modal, Spin, Skeleton } from 'antd';

import { Screen } from '../../components';
import './SubjectsPage.scss';
import { useUser } from '../../hooks/useUser';
import { useState, type SyntheticEvent } from 'react';
import { useDownloadContent, useGetBranchContent, useRemoveContent } from '../../services/content';
import { Link } from 'react-router-dom';
import { useBranch } from '../../hooks/useBranch';
import { type IContent } from '../../types/content';
import { IconEdit, IconTrash, IconDownload, IconChevronDown, IconChevronRight, IconPlus } from '@tabler/icons-react';
import { type ISubject } from '../../types/subject';
import { type ITopic } from '../../types/topic';
import ContentModal from './components/ContentModal/ContentModal';
import Folder from './components/Folder/Folder';
import { AddFolderModal } from './components/AddFolderModal/AddFolderModal';
import clsx from 'clsx';
import { getDocumentViewerPath, isViewableDocument } from '../../utils/getS3FileKey';
import { openDownloadedContent } from '../../utils/openDownloadedContent';

const SubjectsPage = () => {
  const { branch } = useBranch();
  const { user } = useUser();
  const { refetch, grouped, folders: selectFolders, loading: loadingContent } = useGetBranchContent(branch?._id);
  const { isAdmin, isSuperAdmin } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const { removeContent, loading } = useRemoveContent();
  const { downloadContent } = useDownloadContent();
  const [downloading, setDownloading] = useState<string | undefined>(undefined);
  const [removeModal, setRemoveModal] = useState(false);
  const [addFolderModal, setAddFolderModal] = useState(false);
  const [iframeModal, setIframeModal] = useState<string>();
  const [selectedContent, setSelectedContent] = useState<IContent>();

  const [selectedForFolder, setSelectedForFolder] = useState<string>();
  const [expandedSubjects, setExpandedSubjects] = useState<Set<string>>(new Set());
  const [expandedTopics, setExpandedTopics] = useState<Set<string>>(new Set());

  const toggleSubject = (subject: string) => {
    const newExpanded = new Set(expandedSubjects);
    if (newExpanded.has(subject)) {
      newExpanded.delete(subject);
    } else {
      newExpanded.add(subject);
    }
    setExpandedSubjects(newExpanded);
  };

  const toggleTopic = (topic: string) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topic)) {
      newExpanded.delete(topic);
    } else {
      newExpanded.add(topic);
    }
    setExpandedTopics(newExpanded);
  };

  const onAddFolder = (topicId?: string) => {
    setSelectedForFolder(topicId);
    setAddFolderModal(true);
  };

  const onCancel = () => {
    setSelectedContent(undefined);
    setIsOpen(false);
  };

  const onClickAddContent = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(true);
  };

  const handleFinish = async () => {
    setSelectedContent(undefined);
    await refetch();
  };

  const handleRemove = async () => {
    await removeContent(selectedContent?._id as string);
    await refetch();
    setRemoveModal(false);
  };

  const openIframe = (iframeUrl: string) => {
    setIframeModal(iframeUrl);
  };

  const handleDownload = async (contentId: string) => {
    setDownloading(contentId);
    const response = await downloadContent(contentId);

    if (response?.fileData && response?.filename) {
      openDownloadedContent(response);
    }

    setDownloading(undefined);
  };

  return (
    <Screen id="subjects" title="Temario">
      {loadingContent ? (
        <div className="w-full md:mx-0 space-y-4">
          {[1, 2, 3].map((subjectIndex) => (
            <div
              key={subjectIndex}
              className={clsx(
                'rounded-xl border-2 transition-all duration-300 overflow-hidden',
                'bg-white border-gray-200 shadow-md',
                'dark:bg-surface-dark dark:border-borders dark:shadow-lg'
              )}
            >
              <div className="p-4 sm:p-6">
                <Skeleton
                  active
                  title={{ width: '60%' }}
                  paragraph={false}
                  className="mb-4"
                />
                <div className="space-y-3">
                  {[1, 2].map((topicIndex) => (
                    <div
                      key={topicIndex}
                      className={clsx(
                        'rounded-lg border-2 p-3 sm:p-4',
                        'bg-gray-50 border-gray-200',
                        'dark:bg-surface-medium dark:border-borders'
                      )}
                    >
                      <Skeleton
                        active
                        title={{ width: '40%' }}
                        paragraph={{ rows: 2, width: ['100%', '80%'] }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="w-full md:mx-0">
          {
            (isAdmin || isSuperAdmin) && (
              <div className="mb-6 flex justify-end px-4 sm:px-6 md:px-8">
                <button
                  onClick={onClickAddContent}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all duration-300',
                    'bg-blue-600 text-white hover:bg-blue-700',
                    'dark:bg-blue-500 dark:hover:bg-blue-600',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                    'dark:focus:ring-offset-surface-dark',
                    'shadow-sm hover:shadow-md'
                  )}
                >
                  <IconPlus size={18} strokeWidth={2} />
                  Añadir contenido
                </button>
              </div>
            )
          }
          <div className="space-y-4">
            {Object.entries(grouped || {})
              .sort(([subjectA], [subjectB]) => subjectA.localeCompare(subjectB, 'es', { sensitivity: 'base' }))
              .map(([subject, subjectContent]) => {
              const sortable = subjectContent as any;
              const sortedObj = Object.keys(sortable)
                .sort()
                .reduce((acc: any, key) => {
                  acc[key] = sortable[key];
                  return acc;
                }, {});

              const isSubjectExpanded = expandedSubjects.has(subject);

              return (
                <div
                  key={subject}
                  className={clsx(
                    'rounded-xl border-2 transition-all duration-300 overflow-hidden',
                    'bg-white border-gray-200 shadow-md',
                    'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
                    'hover:shadow-lg dark:hover:shadow-xl'
                  )}
                >
                  <button
                    onClick={() => toggleSubject(subject)}
                    className={clsx(
                      'w-full p-4 sm:p-6 flex items-center justify-between transition-colors duration-300',
                      'hover:bg-gray-50 dark:hover:bg-surface-medium'
                    )}
                  >
                    <h3 className={clsx(
                      'text-lg sm:text-xl font-bold transition-colors duration-300',
                      'text-gray-900 dark:text-white'
                    )}>
                      {subject}
                    </h3>
                    {isSubjectExpanded ? (
                      <IconChevronDown size={24} className="text-gray-600 dark:text-gray-400" />
                    ) : (
                      <IconChevronRight size={24} className="text-gray-600 dark:text-gray-400" />
                    )}
                  </button>

                  {isSubjectExpanded && (
                    <div className="px-4 sm:px-6 pb-4 sm:pb-6 space-y-3">
                      {Object.entries(sortedObj as any).map(([topic, topicContent]) => {
                        const [topicName, topicId] = topic.split('***');
                        const content = topicContent as IContent[];
                        if (!content || content.length === 0) return null;

                        const filteredContent = content.filter(({ folder }) => {
                          if (user?.hiddenFolders?.includes(`${topicId}-${folder}`)) {
                            return false;
                          }
                          return true;
                        });

                        const folders: string[] = [];
                        filteredContent.forEach(({ folder }) => {
                          if (folder && !folders.includes(folder)) {
                            folders.push(folder);
                          }
                        });

                        const isTopicExpanded = expandedTopics.has(topic);

                        return (
                          <div
                            key={topic}
                            className={clsx(
                              'rounded-lg border-2 transition-all duration-300',
                              'bg-gray-50 border-gray-200',
                              'dark:bg-surface-medium dark:border-borders'
                            )}
                          >
                            <div className="flex items-center justify-between p-3 sm:p-4">
                              <button
                                onClick={() => toggleTopic(topic)}
                                className={clsx(
                                  'flex items-center gap-2 flex-1 text-left transition-colors duration-300',
                                  'hover:text-blue-600 dark:hover:text-blue-400'
                                )}
                              >
                                {isTopicExpanded ? (
                                  <IconChevronDown size={20} className="text-gray-600 dark:text-gray-400" />
                                ) : (
                                  <IconChevronRight size={20} className="text-gray-600 dark:text-gray-400" />
                                )}
                                <span className={clsx(
                                  'font-semibold text-sm sm:text-base transition-colors duration-300',
                                  'text-gray-900 dark:text-white'
                                )}>
                                  {topicName}
                                </span>
                              </button>
                              {(isAdmin || isSuperAdmin) && (
                                <button
                                  onClick={() => onAddFolder(topicId)}
                                  className={clsx(
                                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300',
                                    'bg-green-600 text-white hover:bg-green-700',
                                    'dark:bg-green-500 dark:hover:bg-green-600',
                                    'focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2',
                                    'dark:focus:ring-offset-surface-dark'
                                  )}
                                >
                                  <IconPlus size={14} strokeWidth={2} />
                                  <span className="hidden sm:inline">Añadir carpeta</span>
                                  <span className="sm:hidden">Carpeta</span>
                                </button>
                              )}
                            </div>

                            {isTopicExpanded && (
                              <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-2">
                                {folders.map((folder) => (
                                  <Folder
                                    key={folder}
                                    openIframe={openIframe}
                                    refresh={refetch}
                                    openModal={setIsOpen}
                                    folder={folder}
                                    content={content}
                                    loading={loading}
                                    setSelectedContent={setSelectedContent}
                                    setRemoveModal={setRemoveModal}
                                    downloading={downloading}
                                    setDownloading={setDownloading}
                                  />
                                ))}
                                {filteredContent.filter(({ folder }) => !folder).map((it) => (
                                  <div
                                    key={it.name}
                                    className={clsx(
                                      'flex items-center justify-between p-3 rounded-lg border transition-all duration-300',
                                      'bg-white border-gray-200',
                                      'dark:bg-surface-dark dark:border-borders',
                                      'hover:shadow-md dark:hover:shadow-lg'
                                    )}
                                  >
                                    <div className="flex-1 min-w-0">
                                      {it.iframeCode && (
                                        <button
                                          onClick={() => openIframe(it.iframeCode!)}
                                          className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                          )}
                                        >
                                          {it.name}
                                        </button>
                                      )}
                                      {(!it.iframeCode && !isViewableDocument(it.url)) && (
                                        <a
                                          href={it.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate block',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                          )}
                                        >
                                          {it.name}
                                        </a>
                                      )}
                                      {(!it.iframeCode && isViewableDocument(it.url)) && (
                                        <Link
                                          to={getDocumentViewerPath(it.url, it.name, Boolean(it.downloadable), it._id)}
                                          className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate block',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                          )}
                                        >
                                          {it.name}
                                        </Link>
                                      )}
                                      {it.type === 'image' && (
                                        <a
                                          href={it.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className={clsx(
                                            'text-sm sm:text-base font-medium transition-colors duration-300 truncate block',
                                            'text-blue-600 hover:text-blue-700',
                                            'dark:text-blue-400 dark:hover:text-blue-300'
                                          )}
                                        >
                                          {it.name}
                                        </a>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                      {it.downloadable && (
                                        <button
                                          onClick={async () => await handleDownload(it._id!)}
                                          disabled={downloading === it._id}
                                          className={clsx(
                                            'p-2 rounded-lg transition-all duration-300',
                                            'text-blue-600 hover:bg-blue-50',
                                            'dark:text-blue-400 dark:hover:bg-blue-900/20',
                                            'disabled:opacity-50 disabled:cursor-not-allowed'
                                          )}
                                        >
                                          {downloading === it._id ? (
                                            <Spin size="small" />
                                          ) : (
                                            <IconDownload size={18} strokeWidth={2} />
                                          )}
                                        </button>
                                      )}
                                      {isAdmin && (
                                        <button
                                          onClick={() => {
                                            setSelectedContent({
                                              ...it,
                                              subject: (it.subject as ISubject)._id!,
                                              topic: (it.topic as ITopic)._id!
                                            });
                                            setTimeout(() => {
                                              setIsOpen(true);
                                            }, 500);
                                          }}
                                          className={clsx(
                                            'p-2 rounded-lg transition-all duration-300',
                                            'text-green-600 hover:bg-green-50',
                                            'dark:text-green-400 dark:hover:bg-green-900/20'
                                          )}
                                        >
                                          <IconEdit size={18} strokeWidth={2} />
                                        </button>
                                      )}
                                      {isAdmin && (
                                        <button
                                          onClick={() => {
                                            setRemoveModal(true);
                                            setSelectedContent(it);
                                          }}
                                          className={clsx(
                                            'p-2 rounded-lg transition-all duration-300',
                                            'text-red-600 hover:bg-red-50',
                                            'dark:text-red-400 dark:hover:bg-red-900/20'
                                          )}
                                        >
                                          <IconTrash size={18} strokeWidth={2} />
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
              })}
          </div>
        </div>
      )}
      {removeModal && (
        <Modal
          open={removeModal}
          onCancel={() => setRemoveModal(false)}
          okButtonProps={{
            danger: true,
            loading
          }}
          onOk={handleRemove}
          okText="Eliminar"
          title="Eliminar temario"
          centered={false}
          className={clsx(
            'remove-content-modal',
            'dark:text-white'
          )}
          style={{
            borderRadius: '16px',
            overflow: 'hidden'
          }}
          maskStyle={{
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          wrapClassName={clsx(
            'remove-content-modal-wrapper',
            'bottom-sheet-mobile'
          )}
        >
          <p className={clsx(
            'text-gray-600 dark:text-gray-300'
          )}>
            {'Esto eliminará este contenido y no podrás recuperarlo. ¿Estás seguro de que quieres continuar?'}
          </p>
        </Modal>
      )}
      {!!iframeModal && (
        <Modal
          open={!!iframeModal}
          onCancel={() => setIframeModal(undefined)}
          onOk={() => setIframeModal(undefined)}
          okText="Cerrar"
          title=""
          centered={false}
          width="90vw"
          style={{
            maxWidth: '800px',
            borderRadius: '16px',
            overflow: 'hidden'
          }}
          maskStyle={{
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          wrapClassName={clsx(
            'iframe-modal-wrapper',
            'bottom-sheet-mobile'
          )}
        >
          {!!iframeModal && (
            <div className="w-full" style={{ paddingBottom: '56.25%', position: 'relative', height: 0 }}>
              <iframe
                src={`${iframeModal}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%'
                }}
                title="Video"
              />
              <script src="https://player.vimeo.com/api/player.js"></script>
            </div>
          )}
        </Modal>
      )}
      {addFolderModal && (
        <AddFolderModal refetch={refetch} isOpen={addFolderModal} onClose={() => setAddFolderModal(false)} selectedForFolder={selectedForFolder} />
      )}
      {isOpen && (
        <ContentModal folders={selectFolders} selectedSubject={selectedContent} onFinish={handleFinish} isOpen={isOpen} title='Contenido' onCancel={onCancel} />
      )}
    </Screen>
  );
};

export { SubjectsPage };
